/**
 * Phase 45 Plan 45-08 — task watcher API client (D-14, Open Q6).
 *
 * Backend contract (45-04):
 *   GET    /api/tasks/:taskId/watchers                → WatcherDto[]
 *   POST   /api/tasks/:taskId/watchers/me             — self-watch sugar
 *   DELETE /api/tasks/:taskId/watchers/me             — self-unwatch sugar
 *   POST   /api/tasks/:taskId/watchers                body: { userId }
 *   DELETE /api/tasks/:taskId/watchers/:userId        → 204
 *
 * Self-watch / self-unwatch use the /me sugar paths so we never need to know
 * the current user's id on the client. Adding/removing others requires
 * PM/admin authz (enforced server-side via Q6 asymmetric matrix).
 */
import { apiGet, apiPost, apiDelete } from './client';

export interface WatcherDto {
  taskId: number;
  // Nullable since Watcher notifications v1 (260702) — external watcher rows
  // carry userId=null and externalUserId set.
  userId: number | null;
  externalUserId: number | null;
  kind: 'user' | 'external_user';
  createdAt: string;
  user: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
    /**
     * 260915-i0x avatar slot, already resolved through the task's project
     * scope by the backend — pass it straight to `<Avatar colorSeq>`. Null for
     * external watchers and for anyone outside the project scope; Avatar then
     * falls back to its name hash.
     */
    avatarColorSeq: number | null;
    /** MKS-203 — relative API path to the effective avatar, or null (same
     * rule as avatarColorSeq above). */
    avatarUrl: string | null;
  };
}

export async function fetchTaskWatchers(taskId: number): Promise<WatcherDto[]> {
  const response = await apiGet<{ data: WatcherDto[] }>(`/tasks/${taskId}/watchers`);
  return response.data;
}

export async function selfWatchTask(taskId: number): Promise<void> {
  await apiPost<{ data: { success: boolean } }>(`/tasks/${taskId}/watchers/me`);
}

export async function selfUnwatchTask(taskId: number): Promise<void> {
  await apiDelete<void>(`/tasks/${taskId}/watchers/me`);
}

export async function addTaskWatcher(taskId: number, userId: number): Promise<void> {
  await apiPost<{ data: { success: boolean } }>(`/tasks/${taskId}/watchers`, { userId });
}

export async function removeTaskWatcher(taskId: number, userId: number): Promise<void> {
  await apiDelete<void>(`/tasks/${taskId}/watchers/${userId}`);
}
